import Chip, { ChipProps } from '@mui/material/Chip'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import CloseIcon from '@mui/icons-material/Close'
import { useCallback, useMemo, useState } from 'react'
import { PersistedErrorHandler } from 'components/Formik/forms'
import useId from '@mui/utils/useId'
import { SxProps } from '@mui/material/styles'
import colors from 'lib/colors'
import { TooltipIfEnabled } from '../TooltipIfEnabled/TooltipIfEnabled'

// Split on commas, tab or newline
const DELIMITER_REGEX = /[,\t\n]+/

// Always strip control characters except \n and \t
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_REGEX = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g

const stripControlCharacters = (text: string) => text.replace(CONTROL_CHARS_REGEX, '')

/**
 * Keep only characters that match the provided allowlist regex.
 * Expects: non-global, per-character regex (e.g., /[A-Za-z0-9 _-]/ or /[\p{L}\p{N}\p{Zs}.-]/u)
 * Must also include the delimiters to accept, otherwise there will be no way to complete a chip
 */
const allowedCharactersOnly = (text: string, allowed?: RegExp | null) => {
  if (!allowed) return text
  return Array.from(text).filter(char => allowed.test(char)).join('')
}

const sanitize = (text: string, allowed?: RegExp | null) =>
  allowedCharactersOnly(stripControlCharacters(text), allowed)

const normalizeChips = (chips: string[], allowed?: RegExp | null) => {
  return Array.from(
    new Set(
      chips
        .map(c => sanitize(String(c), allowed).trim())
        .filter(Boolean)
    )
  )
}

export type DisplayChip = {
  value: string
  key?: React.Key
  tooltip?: string
  chipProps?: Partial<ChipProps>
  onDelete?: () => void
}

interface FreeTextMultiSelectProps {
  label: string
  value: string[]
  setValue: (newValue: string[]) => void
  path?: string
  disabled?: boolean
  options?: string[]
  placeholder?: string
  allowedCharactersRegex?: RegExp | null
  error?: boolean
  required?: boolean
  onFocus?: () => void
  sx?: SxProps
  displayChips?: DisplayChip[]
}

const closeDeleteIcon = <CloseIcon sx={{ color: `${colors.charcoal} !important`, fontSize: '18px !important' }} />

export default function FreeTextMultiSelect({
  label,
  value,
  setValue,
  path = '',
  disabled = false,
  options = [],
  placeholder = '',
  allowedCharactersRegex = null,
  error = false,
  required = false,
  onFocus = () => {},
  sx = {},
  displayChips = [],
}: FreeTextMultiSelectProps) {
  const autoId = useId()
  const [inputValue, setInputValue] = useState('')

  const normalizedValue = useMemo(
    () => normalizeChips(value ?? [], allowedCharactersRegex),
    [allowedCharactersRegex, value]
  )

  const handleChange = useCallback(
    (_e: unknown, newChipValues: Array<string | unknown>) => {
      const stringsOnly = newChipValues.filter(v => typeof v === 'string') as string[]
      setValue(normalizeChips(stringsOnly, allowedCharactersRegex))
    },
    [allowedCharactersRegex, setValue]
  )

  // Per-keystroke enforcement in the text input
  const handleInputChange = useCallback(
    (_e: unknown, next: string, reason: string) => {
      const finalValue = reason === 'input' ? sanitize(next, allowedCharactersRegex) : next
      setInputValue(finalValue)
    },
    [allowedCharactersRegex]
  )

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const raw = e.clipboardData.getData('text/plain') ?? ''
    const cleaned = sanitize(raw, allowedCharactersRegex).trim()

    const newChips = cleaned.split(DELIMITER_REGEX).filter(Boolean)
    if (newChips.length === 0) return

    const merged = normalizeChips([...(normalizedValue ?? []), ...newChips], allowedCharactersRegex)
    setValue(merged)
    setInputValue('')
  }

  const renderedDisplayChips = useMemo(() => {
    return (displayChips ?? []).map((c, i) => (
      <TooltipIfEnabled enabled={!!c.tooltip} label={c.tooltip ?? ''} key={`${c.tooltip}${c.key}`}>
        <Chip
          color='default'
          size='medium'
          variant='filled'
          label={c.value}
          onDelete={disabled ? undefined : c.onDelete}
          deleteIcon={closeDeleteIcon}
          sx={{ m: .5 }}
          {...c.chipProps}
          key={c.key ?? `display-${i}-${c.value}`}
        />
      </TooltipIfEnabled>
    ))
  }, [disabled, displayChips])

  return (
    <Autocomplete<string, true, false, true>
      multiple
      id={`${label || 'chips'}_${autoId}`}
      options={options}
      value={normalizedValue}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      freeSolo
      onChange={handleChange}
      autoSelect
      onFocus={onFocus}
      sx={{
        '.MuiAutocomplete-inputRoot': { minHeight: '48px' },
        '.MuiAutocomplete-input': { paddingY: '2.5px !important' },
        width: '100%',
        ...sx,
      }}
      // render VALUE chips so we can set deleteIcon
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            color='default'
            size='medium'
            variant='filled'
            label={option}
            deleteIcon={closeDeleteIcon}
            {...getTagProps({ index })}
            key={`value-${index}-${option}`}
          />
        ))
      }
      renderInput={(params) => {
        const hasDisplayChips = renderedDisplayChips.length > 0
        const hasTagAdornment = Boolean(params.InputProps.startAdornment)
        const hasStartAdornment = hasDisplayChips || hasTagAdornment

        const textField = (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            error={error}
            onPaste={handlePaste}
            InputProps={{
              ...params.InputProps,
              ...(hasStartAdornment
                ? {
                    startAdornment: (
                      <>
                        {renderedDisplayChips}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }
                : {}),
            }}
          />
        )

        if (path.length > 0) {
          return (
            <PersistedErrorHandler name={path}>
              {textField}
            </PersistedErrorHandler>
          )
        }

        return textField
      }}
    />
  )
}
